import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import TuneIcon from '@mui/icons-material/Tune';
import { Visibility } from '../Panels/Visibility';

import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';

import { AnalysisDrawerLeft, AnalysisDrawerRight, Permissions } from '@/types';
import { AssemblyTabs } from '@/components/Analysis/Viewer/AssemblyTabs';
import { useHavePermission } from '@/hooks';
import { QUERY_DRAWER_COLOR } from '@/constants';
import { FilterSideBar } from '@/components/Analysis/shared/FilterSideBar/FilterSideBar';
import {
  TemplateTypeEnum,
  useAllAssemblyTemplatesForFilterSideBarQuery,
  useGetAssembliesByTemplateFieldsFiltersForEquipmentsTabLazyQuery,
} from '@/__generated__/graphql';
import { SideDrawer } from '@/components/shared/SideDrawer';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { statesLoaded } from '@/state';
import { useMeasurementTool } from '../../modules/measurementTool';
import { closeAll, toggleValue } from '@/utils';
import { useFindViewpointsTool } from '../../modules/findViewpointsTool';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { poiState } from '../../modules/pointOfInterest';
import { selectedSavedBlock as selectedSavedBlockAtom } from '../../modules/CustomBlocks/state';
import * as cuboidState from '@/components/Analysis/modules/cuboids/state';
import { SplitViewPanel } from '../SplitViewPanel';

const ViewpointsDrawerLazy = lazy(() =>
  import('./ViewpointsDrawer').then(({ ViewpointsDrawer }) => ({
    default: ViewpointsDrawer,
  }))
);

const MeasuringToolLazy = lazy(() =>
  import('./MeasuringTool').then(({ MeasuringTool }) => ({
    default: MeasuringTool,
  }))
);

const FindViewpointsToolLazy = lazy(() =>
  import('./FindViewpointsTool').then(({ FindViewpointsTool }) => ({
    default: FindViewpointsTool,
  }))
);

export const Drawers = () => {
  const structureId = useGetStructureId();
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const [previousDrawerLeft, setPreviousDrawerLeft] = useState<AnalysisDrawerLeft>();
  const [filteredAssemblies, setFilteredAssemblies] = useAtom(state.filteredAssemblies);
  const setAssemblyLoading = useSetAtom(state.selectedAssemblyLoading);

  const [drawerLeft, setDrawerLeft] = useAtom(state.drawerLeft);
  const [drawerRight, setDrawerRight] = useAtom(state.drawerRight);
  const setDrawerRightPreviousState = useSetAtom(state.drawerRightPreviousState);
  const [applyFilters, setApplyFilters] = useAtom(state.applyFilters);
  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const [selectedTemplateFilters, setSelectedTemplateFilters] = useAtom(
    state.selectedTemplateFiltersState
  );
  const [selectedDeckIds, setSelectedDeckIds] = useAtom(state.selectedFilteredDeckIds);
  const [selectedPaintRegionIds, setSelectedPaintRegionIds] = useAtom(
    state.selectedFilteredPaintRegionIds
  );
  const [selectedWorkpackIds, setSelectedWorkpackIds] = useAtom(state.selectedFilteredWorkpackIds);
  const selectedPoiTypes = useAtomValue(state.selectedFilteredPoiTypes);

  const selectedAssemblyName = useAtomValue(state.selectedAssemblyName);
  const selectedSpherical = useAtomValue(state.selectedSpherical);
  const isUncontextualised = useAtomValue(state.isUncontextualised);
  const allStatesLoaded = useAtomValue(statesLoaded);
  const sideBySideModeEnabled = useAtomValue(state.sideBySideModeEnabled);
  const setSideBySideModeEnabled = useSetAtom(state.sideBySideModeEnabled);
  const selectedSavedBlock = useAtomValue(selectedSavedBlockAtom);
  const showOnlySelectedPoi = useAtomValue(state.showOnlySelectedPoi);

  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const setAllAssemblyTemplates = useSetAtom(state.allAssemblyTemplates);

  const isIsolatedWorkscope = useAtom(cuboidState.isIsolatedWorkscope);

  const canReadDefects = useHavePermission(Permissions.READ_DEFECTS);

  const { data: templatesData } = useAllAssemblyTemplatesForFilterSideBarQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    skip: !structureId || !!selectedAssemblyTemplateId,
  });

  useEffect(() => {
    const assemblyTemplates = templatesData?.allTemplates;
    if (assemblyTemplates) {
      setAllAssemblyTemplates(templatesData.allTemplates);
      let selectedTemplate = assemblyTemplates[0];
      if (!selectedTemplate) return;
      if (!canReadDefects) {
        selectedTemplate = {
          ...selectedTemplate,
          fields:
            selectedTemplate?.fields?.filter(
              (templateField) =>
                !templateField.permission || templateField.permission !== Permissions.READ_DEFECTS
            ) || [],
        };
      }
    }
  }, [templatesData?.allTemplates, setAllAssemblyTemplates, canReadDefects]);

  useEffect(() => {
    setApplyFilters(true);
  }, [setApplyFilters]);

  const { leftDrawerWidth } = useDrawerWidths();
  const handleLeftToggle = useCallback(
    (property: keyof AnalysisDrawerLeft) => {
      setDrawerLeft(toggleValue<AnalysisDrawerLeft>(drawerLeft, property));
    },
    [drawerLeft, setDrawerLeft]
  );

  const handleRightToggle = useCallback(
    (property: keyof AnalysisDrawerRight) => {
      setDrawerRight(toggleValue<AnalysisDrawerRight>(drawerRight, property));
      setDrawerRightPreviousState(property);
    },
    [drawerRight, setDrawerRight, setDrawerRightPreviousState]
  );

  const leftDrawersClosed = useMemo(() => {
    return !Object.values(drawerLeft).includes(true);
  }, [drawerLeft]);

  const rightDrawersClosed = useMemo(() => {
    return !Object.values(drawerRight).includes(true);
  }, [drawerRight]);

  useEffect(() => {
    if (isUncontextualised !== undefined) {
      setDrawerLeft({
        visibility: isUncontextualised,
        query: !isUncontextualised,
      });
    }
  }, [isUncontextualised, setDrawerLeft]);

  useEffect(() => {
    if (showOnlySelectedPoi) {
      return;
    }
    if (selectedAssemblyId) {
      setDrawerLeft((previousDrawerLeft_) => {
        setPreviousDrawerLeft(previousDrawerLeft_);
        return closeAll(previousDrawerLeft_);
      });
    }
  }, [selectedAssemblyId, setDrawerLeft, showOnlySelectedPoi]);

  useEffect(() => {
    if ((!selectedAssemblyId || selectedAssemblyName) && selectedSpherical) {
      setDrawerLeft({ visibility: false, query: false });
    } else if (!selectedAssemblyId && previousDrawerLeft) {
      setDrawerLeft(previousDrawerLeft);
    }
  }, [
    selectedAssemblyId,
    previousDrawerLeft,
    setDrawerLeft,
    selectedSpherical,
    selectedAssemblyName,
  ]);
  const [fetchFilteredAssemblies] =
    useGetAssembliesByTemplateFieldsFiltersForEquipmentsTabLazyQuery();
  const defaultFiltersForAssemblies = useMemo(() => {
    return [
      selectedTemplateFilters,
      selectedPaintRegionIds,
      selectedDeckIds,
      selectedPoiTypes,
    ].every((filters) => filters.length === 0);
  }, [selectedDeckIds, selectedTemplateFilters, selectedPaintRegionIds, selectedPoiTypes]);

  useEffect(() => {
    if (!applyFilters) return;
    if (selectedAssemblyTemplateId && allStatesLoaded) {
      fetchFilteredAssemblies({
        variables: {
          input: {
            selectedTemplateFilters,
            structureId,
            templateId: selectedAssemblyTemplateId ?? '',
            selectedDeckIds,
            selectedPaintRegionIds: [
              ...selectedPaintRegionIds,
              ...(selectedSavedBlock?.paintRegion?.id ? [selectedSavedBlock.paintRegion.id] : []),
            ],
            selectedWorkpackIds,
            ...(selectedPoiTypes.length > 0 && { selectedPoiTypes }),
          },
        },
        fetchPolicy: 'no-cache',
      })
        .then(({ data }) => {
          setFilteredAssemblies(data?.getAssembliesByTemplateFieldsFilters);
          setApplyFilters(false);
        })
        .finally(() => setAssemblyLoading(false));
    }
  }, [
    defaultFiltersForAssemblies,
    selectedAssemblyTemplateId,
    fetchFilteredAssemblies,
    selectedTemplateFilters,
    structureId,
    setAssemblyLoading,
    selectedDeckIds,
    selectedPaintRegionIds,
    allStatesLoaded,
    applyFilters,
    setApplyFilters,
    selectedWorkpackIds,
    selectedPoiTypes,
    selectedSavedBlock?.paintRegion?.id,
    setFilteredAssemblies,
  ]);

  useEffect(() => {
    if (filteredAssemblies && selectedAssemblyName && !selectedAssemblyId) {
      setSelectedAssemblyId(
        filteredAssemblies.find((assembly) => assembly?.tagName === selectedAssemblyName)?.id ??
          undefined
      );
    }
  }, [filteredAssemblies, selectedAssemblyId, selectedAssemblyName, setSelectedAssemblyId]);

  useEffect(() => {
    if (
      sideBySideModeEnabled &&
      !drawerRight.findViewpointsTool &&
      !drawerRight.equipment &&
      !drawerRight.measuringTool &&
      !drawerRight.viewpoints
    ) {
      setDrawerRight((prev) => ({
        ...prev,
        equipment: true,
      }));
    }
  }, [sideBySideModeEnabled, drawerRight, setDrawerRight]);

  const clearQuery = useCallback(() => {
    setAssemblyLoading(true);
    setFilteredAssemblies(undefined);
  }, [setFilteredAssemblies, setAssemblyLoading]);

  const { isMeasurementMenuVisible } = useMeasurementTool();

  const { isFindViewpointsToolActive } = useFindViewpointsTool();

  useEffect(() => {
    if (
      selectedAssemblyId &&
      !filteredAssemblies?.some((assembly) => assembly.id === selectedAssemblyId)
    ) {
      setSelectedTemplateFilters([]);
      setSelectedDeckIds([]);
      setSelectedPaintRegionIds([]);
      setSelectedWorkpackIds([]);
      setApplyFilters(true);
    }
  }, [
    filteredAssemblies,
    selectedAssemblyId,
    setApplyFilters,
    setSelectedDeckIds,
    setSelectedTemplateFilters,
    setSelectedPaintRegionIds,
    setSelectedWorkpackIds,
  ]);
  return (
    <div data-testid="drawers">
      {editPointOfInterest.state === 'Inactive' && !sideBySideModeEnabled && (
        <AssemblyTabs
          allClosed={rightDrawersClosed}
          isOpen={drawerRight.equipment}
          handleOpen={() => handleRightToggle('equipment')}
        />
      )}
      {editPointOfInterest.state === 'Inactive' && sideBySideModeEnabled && (
        <Suspense>
          <SplitViewPanel
            allClosed={rightDrawersClosed}
            isOpen={drawerRight.equipment}
            handleOpen={() => handleRightToggle('equipment')}
          />
        </Suspense>
      )}
      {editPointOfInterest.state === 'Inactive' && !sideBySideModeEnabled && (
        <Suspense>
          <ViewpointsDrawerLazy
            allClosed={rightDrawersClosed}
            isOpen={drawerRight.viewpoints}
            handleOpen={() => handleRightToggle('viewpoints')}
          />
        </Suspense>
      )}

      {isMeasurementMenuVisible && (
        <Suspense>
          <MeasuringToolLazy
            allClosed={rightDrawersClosed}
            isOpen={drawerRight.measuringTool}
            handleOpen={() => handleRightToggle('measuringTool')}
          />
        </Suspense>
      )}

      {isFindViewpointsToolActive && (
        <Suspense>
          <FindViewpointsToolLazy
            allClosed={rightDrawersClosed}
            isOpen={drawerRight.findViewpointsTool}
            handleOpen={() => handleRightToggle('findViewpointsTool')}
          />
        </Suspense>
      )}
      {(isIsolatedWorkscope || editPointOfInterest.state === 'Inactive') && (
        <Visibility
          allClosed={leftDrawersClosed}
          isOpen={drawerLeft.visibility}
          handleOpen={() => handleLeftToggle('visibility')}
          data-testid="visibility-drawer"
        />
      )}

      {!selectedSpherical && !selectedAssemblyId && editPointOfInterest.state === 'Inactive' && (
        <SideDrawer
          allDrawersClosed={leftDrawersClosed}
          isOpen={drawerLeft.query}
          handleOpen={() => handleLeftToggle('query')}
          icon={isUncontextualised ? undefined : <TuneIcon />}
          position="left"
          drawerColor={QUERY_DRAWER_COLOR}
          width={leftDrawerWidth}
          label="Query"
          iconStyles={{ top: 65, zIndex: '102 !important' }}
          data-testid="filter-drawer"
        >
          <FilterSideBar onClear={clearQuery} />
        </SideDrawer>
      )}
    </div>
  );
};
